import { Info } from 'lucide-react';
import { cn } from '@nepal-ecom/ui';

/**
 * Single source of truth for image dimensions across the platform.
 * Customer-web should consume the same numbers when rendering.
 */
export const IMAGE_SPECS = {
  productPhoto: {
    label: 'Product photo',
    dimensions: '1200 × 1200 px',
    ratio: '1:1 (square)',
    maxSize: '2 MB',
    formats: 'JPG · PNG · WebP',
  },
  productGalleryMin: {
    label: 'Minimum size',
    dimensions: '800 × 800 px',
    ratio: '1:1',
    maxSize: '2 MB',
    formats: 'JPG · PNG · WebP',
  },
  storeBanner: {
    label: 'Store banner',
    dimensions: '1600 × 320 px',
    ratio: '5:1',
    maxSize: '2 MB',
    formats: 'JPG · PNG · WebP',
  },
  storeLogo: {
    label: 'Store logo',
    dimensions: '512 × 512 px',
    ratio: '1:1 (transparent PNG)',
    maxSize: '500 KB',
    formats: 'PNG · SVG · WebP',
  },
  ogImage: {
    label: 'Social share (OG)',
    dimensions: '1200 × 630 px',
    ratio: '1.91:1',
    maxSize: '1 MB',
    formats: 'JPG · PNG',
  },
  platformLogo: {
    label: 'Platform logo',
    dimensions: '512 × 128 px',
    ratio: '4:1 (transparent PNG)',
    maxSize: '500 KB',
    formats: 'PNG · SVG',
  },
  favicon: {
    label: 'Favicon',
    dimensions: '512 × 512 px',
    ratio: '1:1',
    maxSize: '200 KB',
    formats: 'PNG · ICO',
  },
  promoBanner: {
    label: 'Home promo banner',
    dimensions: '1920 × 540 px',
    ratio: '32:9',
    maxSize: '2 MB',
    formats: 'JPG · PNG · WebP',
  },
  document: {
    label: 'Document',
    dimensions: 'Any',
    ratio: 'Any',
    maxSize: '5 MB',
    formats: 'PDF · JPG · PNG',
  },
} as const;

export type ImageSpecKey = keyof typeof IMAGE_SPECS;

interface ImageSpecProps {
  spec: ImageSpecKey;
  variant?: 'inline' | 'card';
  className?: string;
}

/**
 * Renders a compact "1200×1200 · 1:1 · ≤2 MB · JPG/PNG/WebP" hint.
 * Use `variant="card"` for upload empty-states; `inline` for label hints.
 */
export function ImageSpec({ spec, variant = 'inline', className }: ImageSpecProps) {
  const s = IMAGE_SPECS[spec];

  if (variant === 'card') {
    return (
      <div className={cn('rounded-lg bg-slate-50 ring-1 ring-slate-200 p-2.5', className)}>
        <div className="flex items-start gap-2">
          <Info className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] leading-relaxed text-slate-600">
            <p className="font-semibold text-slate-700">{s.label} · {s.dimensions}</p>
            <p>
              <span className="text-slate-500">Ratio</span> {s.ratio}
              <span className="text-slate-300 mx-1.5">·</span>
              <span className="text-slate-500">Max</span> {s.maxSize}
              <span className="text-slate-300 mx-1.5">·</span>
              {s.formats}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[11px] text-slate-500 font-medium tabular-nums', className)}>
      <Info className="h-3 w-3 text-slate-400" />
      <span className="text-slate-700">{s.dimensions}</span>
      <span className="text-slate-300">·</span>
      <span>{s.ratio}</span>
      <span className="text-slate-300">·</span>
      <span>≤ {s.maxSize}</span>
      <span className="text-slate-300">·</span>
      <span>{s.formats}</span>
    </span>
  );
}
